import OtpPasswordQuery from '@/services/query/otpPassword/OtpPasswordQuery.services';
import OtpPasswordCommand from '@/services/command/otpPassword/OtpPasswordCommand.services';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
    Box,
    Table,
    TableCell,
    TableRow,
    TableHead,
    Paper,
    TableContainer,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    IconButton,
    TextField,
    Typography,
    Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SCUILoadingLinear } from '@/atomic/atoms/SCUILoadingLinear/SCUILoadingLinear';
import { SMSP } from './smsPassword.classes';

export const SMSPassword = ({ selectedCountry }: { selectedCountry: string }) => {
    const countries = JSON.parse(localStorage.getItem('countryData') || '{}')?.boSupportedCountries || [];
    const [countryRateLimit, setCountryRateLimit] = useState<any>([]);
    const [requestLogs, setRequestLogs] = useState<any>([]);
    const [expanded, setExpanded] = useState<string | false>(false);
    const [isLoading, setIsLoading] = useState(false);

    // Individual hour editing state
    const [editingHour, setEditingHour] = useState<string | null>(null);
    const [editingValue, setEditingValue] = useState<number>(0);
    const [hoveredHour, setHoveredHour] = useState<string | null>(null);

    const { t } = useTranslation();

    const selectedCountryData = useMemo(
        () => countries.find((country: any) => country.countryId === selectedCountry),
        [countries, selectedCountry]
    );

    useEffect(() => {
        if (selectedCountry) {
            getCountryRateLimit();
        }
    }, [selectedCountry]);

    useEffect(() => {
        if (expanded) {
            getRequestLogs();
        }
    }, [expanded]);

    const getCountryRateLimit = () => {
        setIsLoading(true);
        OtpPasswordQuery.GetCountryRateLimit({ countryId: selectedCountry })
            .then((res: any) => {
                setCountryRateLimit(res.data);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const getRequestLogs = () => {
        if (!expanded) return;
        const index = parseInt(expanded.replace('panel-', ''));
        const endpoint = (countryRateLimit || [])[index]?.endpoint;

        if (!endpoint) return;
        setIsLoading(true);

        OtpPasswordQuery.GetRequestLogs({
            endpoint,
            countryId: selectedCountry,
            startDate: new Date().toISOString().split('T')[0],
        })
            .then((res: any) => {
                setRequestLogs(res.data);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
        // Reset editing state when accordion changes
        setEditingHour(null);
        setEditingValue(0);
    };

    // Get current user info
    const getCurrentUser = () => {
        try {
            const checkOperator = JSON.parse(localStorage.getItem('checkOperator') || '{}');
            return {
                userId: checkOperator?.applicationUserId || null,
            };
        } catch {
            return {
                userId: null,
            };
        }
    };

    const handleEditHourClick = (hourString: string, currentLimit: number) => {
        setEditingHour(hourString);
        setEditingValue(currentLimit);
    };

    const handleCancelHourEdit = () => {
        setEditingHour(null);
        setEditingValue(0);
    };

    const handleSaveHourLimit = (hourString: string) => {
        const currentUser = getCurrentUser();
        const expandedIndex = expanded ? parseInt(expanded.replace('panel-', '')) : 0;
        const currentItem = (countryRateLimit || [])[expandedIndex];

        // Single hour/limit object payload
        const payload = {
            countryId: Number(selectedCountry),
            endpoint: currentItem?.endpoint,
            hourLimit: {
                hour: hourString,
                limit: Number(editingValue),
            },
            userId: currentUser.userId,
        };

        setIsLoading(true);

        OtpPasswordCommand.UpdateEndpointRateLimit(payload)
            .then(() => {
                Swal.fire({
                    title: t('general.success'),
                    icon: 'success',
                    showConfirmButton: false,
                    timer: 2000,
                });
                setEditingHour(null);
                setEditingValue(0);
                // Refresh data
                getCountryRateLimit();
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <Box className={SMSP.page} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <div className={SMSP.loadingSection}>
                <SCUILoadingLinear display={isLoading} />
            </div>
            <TableContainer
                className={SMSP.tableContainer}
                sx={{ width: '100%', boxShadow: 3 }}
                component={Paper}
            >
                <Table className={SMSP.table}>
                    <TableHead className={SMSP.tableHead}>
                        <TableRow className={SMSP.tableHeadRow}>
                            <TableCell
                                className={SMSP.tableHeadCellCountry}
                                sx={{ fontWeight: 'bold', fontSize: '14px', width: '20%', p: 2 }}
                            >
                                {t('otpPassword.country')}
                            </TableCell>
                            <TableCell
                                className={SMSP.tableHeadCellEndpoint}
                                sx={{ fontWeight: 'bold', fontSize: '14px', width: '40%', p: 2 }}
                            >
                                {t('otpPassword.endpoint')}
                            </TableCell>
                            <TableCell
                                className={SMSP.tableHeadCellPlatform}
                                sx={{ fontWeight: 'bold', fontSize: '14px', width: '40%', p: 2 }}
                            >
                                {t('otpPassword.platform')}
                            </TableCell>
                            <TableCell
                                className={SMSP.tableHeadCellActions}
                                sx={{ fontWeight: 'bold', fontSize: '14px', width: '48px', p: 2 }}
                            ></TableCell>
                        </TableRow>
                    </TableHead>
                </Table>
                <Box className={SMSP.accordionsWrap}>
                    {(countryRateLimit || []).map((item: any, index: any) => (
                        <Accordion
                            className={SMSP.accordion}
                            key={item.endpoint}
                            expanded={expanded === `panel-${index}`}
                            onChange={handleAccordionChange(`panel-${index}`)}
                            disableGutters
                            elevation={0}
                            sx={{
                                '&:before': {
                                    display: 'none',
                                },
                                borderTop: index === 0 ? 'none' : '1px solid rgba(224, 224, 224, 1)',
                                '&:not(:last-child)': {
                                    borderBottom: 0,
                                },
                                '&.Mui-expanded': {
                                    margin: 0,
                                },
                            }}
                        >
                            <AccordionSummary
                                className={SMSP.accordionSummary}
                                expandIcon={<ExpandMoreIcon className={SMSP.accordionExpandIcon} />}
                                aria-controls={`panel-${index}-content`}
                                id={`panel-${index}`}
                                sx={{
                                    minHeight: '48px',
                                    '&.Mui-expanded': {
                                        minHeight: '48px',
                                    },
                                    '& .MuiAccordionSummary-content': {
                                        margin: 0,
                                        '&.Mui-expanded': {
                                            margin: 0,
                                        },
                                    },
                                    px: 2,
                                    py: 1.5,
                                }}
                            >
                                <Box
                                    className={SMSP.accordionSummaryLayout}
                                    sx={{ display: 'flex', width: '100%', alignItems: 'center' }}
                                >
                                    <Box
                                        className={SMSP.summaryCountryCol}
                                        sx={{ width: '20%', display: 'flex', alignItems: 'center' }}
                                    >
                                        {selectedCountryData?.flagUrl && (
                                            <img
                                                className={SMSP.summaryCountryFlagImg}
                                                src={selectedCountryData.flagUrl}
                                                alt={selectedCountryData.countryCode}
                                                style={{
                                                    width: '30px',
                                                    height: '20px',
                                                    marginRight: '8px',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        )}
                                        <Typography className={SMSP.summaryCountryText} sx={{ fontSize: '14px' }}>
                                            {selectedCountry} | {selectedCountryData?.countryName}
                                        </Typography>
                                    </Box>
                                    <Box className={SMSP.summaryEndpointCol} sx={{ width: '40%' }}>
                                        <Typography className={SMSP.summaryEndpointText} sx={{ fontSize: '14px' }}>
                                            {item.endpoint}
                                        </Typography>
                                    </Box>
                                    <Box className={SMSP.summaryPlatformCol} sx={{ width: '40%' }}>
                                        <Typography
                                            className={SMSP.summaryPlatformText}
                                            sx={{ fontSize: '14px', paddingLeft: '8px' }}
                                        >
                                            {item.platformName}
                                        </Typography>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails
                                className={SMSP.accordionDetails}
                                sx={{
                                    p: 2,
                                    border: '1px solid #e0e0e0',
                                    borderTop: '1px solid rgba(224, 224, 224, 1)',
                                    backgroundColor: '#f7f7f7',
                                }}
                            >
                                <Box className={SMSP.detailsInner}>
                                    <Box
                                        className={SMSP.requestLogsHeader}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mb: 3,
                                        }}
                                    >
                                        <Typography
                                            className={SMSP.requestLogsTitle}
                                            sx={{ fontSize: '14px', fontWeight: 'bold', marginRight: '16px' }}
                                        >
                                            {t('otpPassword.requestLogsDaily')}
                                        </Typography>
                                        <Tooltip title={t('otpPassword.utcTimezoneInfo')} arrow placement="top">
                                            <InfoOutlinedIcon
                                                className={SMSP.utcInfoIcon}
                                                sx={{
                                                    fontSize: '20px',
                                                    color: '#666',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        color: '#1976d2',
                                                    },
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                    <Box
                                        className={SMSP.hoursGrid}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(6, 1fr)',
                                            gap: '16px',
                                            width: '100%',
                                        }}
                                    >
                                        {Array.from({ length: 24 }).map((_, idx) => {
                                            // Get the current item's rate limit values from countryRateLimit
                                            const expandedIndex = expanded
                                                ? parseInt(expanded.replace('panel-', ''))
                                                : 0;
                                            const currentItem = (countryRateLimit || [])[expandedIndex];

                                            // Find the limit for this hour from the values array
                                            const hourString = String(idx).padStart(2, '0');
                                            const limitData = currentItem?.values?.find(
                                                (v: any) => v.hour === hourString
                                            );
                                            const limit = limitData?.limit || 0;

                                            // Find request log data for this hour by parsing windowStartUtc
                                            const logData = (requestLogs || []).find((log: any) => {
                                                if (!log.windowStartUtc) return false;
                                                // Extract hour from ISO datetime string (e.g., "2025-10-31T13:00:00Z" -> 13)
                                                const logHour = parseInt(
                                                    log.windowStartUtc.split('T')[1].split(':')[0]
                                                );
                                                return logHour === idx;
                                            });
                                            const requestCount = logData?.requestCount || 0;

                                            // Determine color based on request count vs limit
                                            let barColor = '#000000';
                                            if (requestCount === 0) {
                                                barColor = '#d1d5db'; // Gray for no requests
                                            } else if (limit === 0) {
                                                barColor = '#000000'; // Black if no limit set
                                            } else {
                                                const percentage = (requestCount / limit) * 100;
                                                if (percentage >= 90) {
                                                    barColor = '#ef4444'; // Red when 90% or more of limit
                                                } else if (percentage >= 50) {
                                                    barColor = '#eab308'; // Yellow when 50% to 90% of limit
                                                } else {
                                                    barColor = '#000000'; // Black when less than 50%
                                                }
                                            }

                                            // Calculate bar width percentage based on limit (0-100%)
                                            const widthPercent =
                                                limit > 0 ? Math.min((requestCount / limit) * 100, 100) : 0;

                                            const isEditing = editingHour === hourString;
                                            const isHovered = hoveredHour === hourString;

                                            return (
                                                <Box
                                                    key={idx}
                                                    className={SMSP.hourCard}
                                                    data-hour={hourString}
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-start',
                                                        position: 'relative',
                                                        p: 1,
                                                        borderRadius: '8px',
                                                        backgroundColor:
                                                            isHovered || isEditing ? '#fff' : 'transparent',
                                                        boxShadow:
                                                            isHovered || isEditing
                                                                ? '0 2px 8px rgba(0,0,0,0.1)'
                                                                : 'none',
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                    onMouseEnter={() => setHoveredHour(hourString)}
                                                    onMouseLeave={() => setHoveredHour(null)}
                                                >
                                                    {/* Time label */}
                                                    <Typography
                                                        className={SMSP.hourTimeLabel}
                                                        sx={{
                                                            fontSize: '11px',
                                                            color: '#666',
                                                            mb: 0.5,
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {`${String(idx).padStart(2, '0')}:00`}
                                                    </Typography>

                                                    {isEditing ? (
                                                        // Edit Mode for this specific hour
                                                        <Box className={SMSP.hourEditWrap} sx={{ width: '100%' }}>
                                                            <TextField
                                                                className={SMSP.hourLimitInput}
                                                                type="number"
                                                                size="small"
                                                                value={editingValue}
                                                                onChange={(e) =>
                                                                    setEditingValue(parseInt(e.target.value) || 0)
                                                                }
                                                                autoFocus
                                                                sx={{
                                                                    width: '100%',
                                                                    mb: 1,
                                                                    '& .MuiInputBase-input': {
                                                                        fontSize: '12px',
                                                                        padding: '4px 8px',
                                                                        textAlign: 'center',
                                                                    },
                                                                }}
                                                                slotProps={{
                                                                    htmlInput: {
                                                                        min: 0,
                                                                        step: 1,
                                                                    },
                                                                }}
                                                            />
                                                            <Box
                                                                className={SMSP.hourEditActions}
                                                                sx={{
                                                                    display: 'flex',
                                                                    gap: 0.5,
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <IconButton
                                                                    className={SMSP.hourSaveButton}
                                                                    size="small"
                                                                    onClick={() => handleSaveHourLimit(hourString)}
                                                                    sx={{
                                                                        backgroundColor: '#4caf50',
                                                                        color: '#fff',
                                                                        '&:hover': {
                                                                            backgroundColor: '#388e3c',
                                                                        },
                                                                        width: '28px',
                                                                        height: '28px',
                                                                    }}
                                                                >
                                                                    <SaveIcon sx={{ fontSize: '16px' }} />
                                                                </IconButton>
                                                                <IconButton
                                                                    className={SMSP.hourCancelButton}
                                                                    size="small"
                                                                    onClick={handleCancelHourEdit}
                                                                    sx={{
                                                                        backgroundColor: '#f44336',
                                                                        color: '#fff',
                                                                        '&:hover': {
                                                                            backgroundColor: '#d32f2f',
                                                                        },
                                                                        width: '28px',
                                                                        height: '28px',
                                                                    }}
                                                                >
                                                                    <CancelIcon sx={{ fontSize: '16px' }} />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <>
                                                            {/* View Mode: Show Progress Bar */}
                                                            {/* Progress bar container */}
                                                            <Box
                                                                className={SMSP.progressBarTrack}
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '8px',
                                                                    backgroundColor: '#e0e0e0',
                                                                    borderRadius: '4px',
                                                                    overflow: 'hidden',
                                                                    mb: 0.5,
                                                                }}
                                                            >
                                                                {/* Progress bar fill */}
                                                                <Box
                                                                    className={SMSP.progressBarFill}
                                                                    sx={{
                                                                        width: `${widthPercent}%`,
                                                                        height: '100%',
                                                                        backgroundColor: barColor,
                                                                        transition: 'width 0.5s ease',
                                                                    }}
                                                                />
                                                            </Box>

                                                            {/* Count label and edit button */}
                                                            <Box
                                                                className={SMSP.hourCountRow}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <Typography
                                                                    className={SMSP.hourCountText}
                                                                    sx={{
                                                                        fontSize: '10px',
                                                                        color: '#666',
                                                                        fontWeight: 400,
                                                                    }}
                                                                >
                                                                    {requestCount}/{limit}
                                                                </Typography>

                                                                {/* Edit icon - only visible on hover */}
                                                                {isHovered && (
                                                                    <IconButton
                                                                        className={SMSP.hourEditButton}
                                                                        size="small"
                                                                        onClick={() =>
                                                                            handleEditHourClick(hourString, limit)
                                                                        }
                                                                        sx={{
                                                                            p: 0.25,
                                                                            backgroundColor: '#1976d2',
                                                                            color: '#fff',
                                                                            '&:hover': {
                                                                                backgroundColor: '#1565c0',
                                                                            },
                                                                            width: '20px',
                                                                            height: '20px',
                                                                        }}
                                                                    >
                                                                        <EditIcon sx={{ fontSize: '12px' }} />
                                                                    </IconButton>
                                                                )}
                                                            </Box>
                                                        </>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </TableContainer>
        </Box>
    );
};
